import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
    Check,
    CheckCircle2,
    Loader2,
    MessageSquare,
    Reply,
    Send,
    X,
} from "lucide-react";
import { toast } from "sonner";
import { crud, dosyaNotApi } from "../lib/api";
import { useAuth } from "../lib/auth";

function fmt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("tr-TR");
    } catch {
        return iso;
    }
}

export default function SigortaDosyaNotDrawer({ open, onClose, dosyaId, dosyaNo, onChanged }) {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [metin, setMetin] = useState("");
    const [saving, setSaving] = useState(false);
    const [replyTo, setReplyTo] = useState(null);

    const load = useCallback(async () => {
        if (!dosyaId) return;
        setLoading(true);
        try {
            const data = await crud.list("dosya-yorumlari", { sigorta_dosyasi: dosyaId });
            setItems(Array.isArray(data) ? data : data.results || []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, [dosyaId]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    async function kaydet(e) {
        e.preventDefault();
        if (!metin.trim()) return;
        setSaving(true);
        try {
            if (replyTo) {
                await dosyaNotApi.cevapla(replyTo.id, metin.trim());
            } else {
                await crud.create("dosya-yorumlari", {
                    sigorta_dosyasi: dosyaId,
                    metin: metin.trim(),
                    kullanici: user?.id,
                    hatirlat: true,
                });
            }
            setMetin("");
            setReplyTo(null);
            toast.success("Not kaydedildi");
            await load();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    }

    async function markOkundu(y) {
        try {
            await dosyaNotApi.okundu(y.id);
            await load();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "İşlem başarısız");
        }
    }

    async function markCozuldu(y, cozuldu = true) {
        try {
            await dosyaNotApi.cozuldu(y.id, cozuldu);
            toast.success(cozuldu ? "Not halledildi olarak işaretlendi" : "Beklemeye alındı");
            await load();
            onChanged?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "İşlem başarısız");
        }
    }

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[420] flex justify-end"
            data-stop-row-click
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
        >
            <button
                type="button"
                className="absolute inset-0 bg-black/60"
                aria-label="Kapat"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={onClose}
            />
            <aside
                className="relative z-[421] w-full max-w-md h-full bg-[#0a1018] border-l border-white/10 shadow-2xl flex flex-col animate-fade-in"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">
                            Dosya notları
                        </div>
                        <div className="text-sm font-semibold text-white flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-[#D4AF37]" />
                            {dosyaNo || `#${dosyaId}`}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="h-8 w-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-slate-400"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {loading ? (
                        <div className="flex justify-center py-8 text-slate-500">
                            <Loader2 className="h-6 w-6 animate-spin" />
                        </div>
                    ) : items.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-6">
                            Henüz operasyon notu yok. İlk notu aşağıdan ekleyin — silinmez, arşivlenir.
                        </p>
                    ) : (
                        items.map((y) => (
                            <div
                                key={y.id}
                                className={`rounded-xl border p-3 text-sm ${
                                    y.cozuldu
                                        ? "border-emerald-500/25 bg-emerald-500/5"
                                        : y.okundu
                                          ? "border-white/10 bg-[#121A2F]/50"
                                          : "border-sky-500/30 bg-sky-500/5"
                                }`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="text-[10px] text-slate-500">
                                        {fmt(y.olusturma_tarihi)} · {y.kullanici_label || "—"}
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        {!y.okundu ? (
                                            <button
                                                type="button"
                                                title="Okundu"
                                                onClick={() => markOkundu(y)}
                                                className="p-1 rounded hover:bg-white/10 text-sky-300"
                                            >
                                                <Check className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                        {!y.cozuldu ? (
                                            <button
                                                type="button"
                                                title="Halledildi"
                                                onClick={() => markCozuldu(y, true)}
                                                className="p-1 rounded hover:bg-white/10 text-emerald-300"
                                            >
                                                <CheckCircle2 className="h-3.5 w-3.5" />
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                title="Tekrar beklemeye al"
                                                onClick={() => markCozuldu(y, false)}
                                                className="text-[9px] text-slate-500 hover:text-amber-300 px-1"
                                            >
                                                Geri al
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            title="Alıntıyla cevapla"
                                            onClick={() => {
                                                setReplyTo(y);
                                                setMetin("");
                                            }}
                                            className="p-1 rounded hover:bg-white/10 text-slate-400"
                                        >
                                            <Reply className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                                {y.ust_yorum_ozet ? (
                                    <div className="text-[10px] text-slate-500 border-l-2 border-[#D4AF37]/40 pl-2 mb-2 italic line-clamp-2">
                                        ↳ {y.ust_yorum_ozet}
                                    </div>
                                ) : null}
                                <p className="text-slate-200 whitespace-pre-wrap text-xs leading-relaxed">
                                    {y.metin}
                                </p>
                                {y.cozuldu && y.cozen_label ? (
                                    <div className="text-[10px] text-emerald-400/80 mt-2">
                                        Halledildi · {y.cozen_label}
                                        {y.cozulme_tarihi ? ` · ${fmt(y.cozulme_tarihi)}` : ""}
                                    </div>
                                ) : null}
                                {y.okundu && y.okuyan_label && !y.cozuldu ? (
                                    <div className="text-[10px] text-slate-500 mt-1">
                                        Okundu · {y.okuyan_label}
                                    </div>
                                ) : null}
                            </div>
                        ))
                    )}
                </div>

                <form
                    onSubmit={kaydet}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    className="p-4 border-t border-white/10 space-y-2"
                >
                    {replyTo ? (
                        <div className="text-[10px] text-[#D4AF37] flex items-center justify-between gap-2 bg-[#D4AF37]/10 rounded px-2 py-1">
                            <span className="truncate">Alıntılı cevap: #{replyTo.id}</span>
                            <button type="button" onClick={() => setReplyTo(null)} className="text-slate-400">
                                <X className="h-3 w-3" />
                            </button>
                        </div>
                    ) : null}
                    <textarea
                        value={metin}
                        onChange={(e) => setMetin(e.target.value)}
                        placeholder="Operasyon notu — günlük hatırlatılır, silinmez…"
                        rows={3}
                        className="w-full bg-[#0B0F19] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 resize-none focus:border-[#D4AF37] outline-none"
                    />
                    <button
                        type="submit"
                        disabled={saving || !metin.trim()}
                        className="w-full h-10 rounded-lg bg-[#D4AF37] text-slate-900 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                        {replyTo ? "Cevap gönder" : "Not kaydet"}
                    </button>
                </form>
            </aside>
        </div>,
        document.body,
    );
}
